import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import { fetchCourseListForWebThunk } from "slices/CourseWeb/thunk";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type { RootState } from "store";
import DashboardHeader from "../../Components/Common/DashboardHeader";

const selectCourseWebState = (state: RootState) => (state as any).courseWeb;

const courseDataSelector = createSelector(selectCourseWebState, (courseWeb) => ({
  courseData: courseWeb.courses,
  loading: courseWeb.loading,
}));

const CourseDetailWeb = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { courseData, loading } = useSelector(courseDataSelector);
  const [expandedWeek, setExpandedWeek] = useState<number | null>(null);
  const [course, setCourse] = useState<any>(null);

  // Check if course already exists in Redux state first
  useEffect(() => {
    if (id && courseData && courseData.length > 0) {
      const foundCourse = courseData.find((c: any) => c.id === parseInt(id));
      if (foundCourse) {
        setCourse(foundCourse);
        return; // Don't fetch if course already exists in state
      }
    }
    
    // Only fetch if course is not in Redux state
    if (id && (!courseData || courseData.length === 0 || !courseData.find((c: any) => c.id === parseInt(id)))) {
      dispatch(
        fetchCourseListForWebThunk({
          page: 1,
          per_page: 10,
          from_date: "",
          to_date: "",
          course_id: parseInt(id),
          searchkey: "",
        })
      );
    }
  }, [dispatch, id, courseData]);

  const toggleWeek = (index: number) => {
    setExpandedWeek(expandedWeek === index ? null : index);
  };

  document.title = course ? `${course.course_name} - Amanat (Web)` : "Course Details (Web) - Amanat";

  if (loading) {
    return (
      <div className="course-detail-page">
        <div className="loading-state">Loading course details...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="course-detail-page">
        <div className="error-state">Course not found</div>
      </div>
    );
  }

  const currentPrice = course.price || 0;

  return (
    <div className="course-detail-page">
      <DashboardHeader />

      <div className="breadcrumbs">
        <div className="container">
          <a href="https://amanatfintech.com/" target="_blank" rel="noopener noreferrer">Home</a>
          <span className="separator"> &gt; </span>
          <Link to="/services">Browse</Link>
          <span className="separator"> &gt; </span>
          <span>Courses</span>
        </div>
      </div>

      <main className="course-main">
        <div className="container">
          <section className="course-hero">
            <div className="hero-image">
              <img 
                src={course.thumbnail_url || "https://via.placeholder.com/600x400"} 
                alt={course.course_name}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "https://via.placeholder.com/600x400";
                }}
              />
            </div>
            <div className="hero-content">
              <h1 className="course-title">{course.course_name}</h1>
              <p className="course-description">
                {course.description || "This comprehensive program teaches fundamental and advanced data analysis skills."}
              </p>
              
              <div className="course-details">
                <div className="detail-item">
                  <i className="ri-calendar-line"></i>
                  <span>{course.noofweek || "12"} week</span>
                </div>
                <div className="detail-item">
                  <i className="ri-time-line"></i>
                  <span>{course.hourperday || "5"} hrs/day</span>
                </div>
              </div>

              <div className="pricing-section">
                <div className="price-info">
                  <span className="current-price">BD {currentPrice.toFixed(0)}</span>
                </div>
              </div>

              <div className="action-buttons">
                <Button 
                  className="add-to-cart-btn"
                  onClick={() => {
                    const cartItem = {
                      id: course.id,
                      course_name: course.course_name,
                      thumbnail_url: course.thumbnail_url,
                      noofweek: course.noofweek || "12",
                      hourperday: course.hourperday || "5",
                      course_level: course.course_level || "Beginner",
                      price: course.price || 0,
                      originalPrice: course.price || 0,
                      discount: 0,
                      quantity: 1
                    };
                    
                    const existingCart = sessionStorage.getItem("webCartItems");
                    let cartItems = existingCart ? JSON.parse(existingCart) : [];
                    
                    const existingIndex = cartItems.findIndex((item: any) => item.id === course.id);
                    if (existingIndex !== -1) {
                      toast.info("Already added in the cart");
                      return;
                    }
                    
                    // Item doesn't exist, add it
                    cartItems.push(cartItem);
                    sessionStorage.setItem("webCartItems", JSON.stringify(cartItems));
                    // Optional: dispatch a custom event if you later want a separate web cart badge
                    window.dispatchEvent(new Event("webCartUpdated"));
                    toast.success("Item added to the cart successfully");
                  }}
                >
                  Add to Cart
                </Button>
                <Button 
                  className="buy-now-btn"
                  onClick={() => {
                    const buyNowItem = {
                      id: course.id,
                      course_name: course.course_name,
                      thumbnail_url: course.thumbnail_url,
                      noofweek: course.noofweek || "12",
                      hourperday: course.hourperday || "5",
                      course_level: course.course_level || "Beginner",
                      price: course.price || 0,
                      originalPrice: course.price || 0,
                      discount: 0,
                      quantity: 1
                    };
                    
                    sessionStorage.setItem("webBuyNowItems", JSON.stringify([buyNowItem]));
                    navigate("/services/buynow");
                  }}
                >
                  Buy Now
                </Button>
              </div>
            </div>
          </section>

          {course.key_summary && course.key_summary.length > 0 && (
            <section className="course-highlights">
              {course.key_summary.map((highlight: any, index: number) => (
                <div key={index} className="highlight-card">
                  <div className="highlight-icon">
                    {index === 0 && <i className="ri-lightbulb-line"></i>}
                    {index === 1 && <i className="ri-cpu-line"></i>}
                    {index === 2 && <i className="ri-calendar-check-line"></i>}
                    {index === 3 && <i className="ri-target-line"></i>}
                    {index > 3 && <i className="ri-star-line"></i>}
                  </div>
                  <h3 className="highlight-title">{highlight.head_line || "Feature"}</h3>
                  <p className="highlight-text">{highlight.keysummary || highlight.key_summary || ""}</p>
                </div>
              ))}
            </section>
          )}

          <div className="syllabus-skills-wrapper">
            {course.syllabus && course.syllabus.length > 0 && (
              <section className="syllabus-section">
                <h2 className="section-title">Syllabus</h2>
                <div className="syllabus-list">
                  {course.syllabus.map((syllabusItem: any, syllabusIndex: number) => {
                    if (!syllabusItem.syllabusdetail || syllabusItem.syllabusdetail.length === 0) {
                      return null;
                    }
                    
                    return syllabusItem.syllabusdetail.map((week: any, weekIndex: number) => {
                      const globalIndex = syllabusIndex * 100 + weekIndex;
                      const isExpanded = expandedWeek === globalIndex;
                      
                      return (
                        <div key={globalIndex} className="syllabus-item">
                          <div 
                            className="syllabus-header"
                            onClick={() => toggleWeek(globalIndex)}
                          >
                            <div className="week-info">
                              <h3 className="week-title">{week.weekname || `Week ${weekIndex + 1}`}</h3>
                              {week.title && <p className="week-subtitle">{week.title}</p>}
                            </div>
                            <i className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line`}></i>
                          </div>
                          {isExpanded && (
                            <div className="syllabus-content">
                              <div className="week-number">{week.weekname || `Week ${weekIndex + 1}`}</div>
                              {week.detail && week.detail.length > 0 && (
                                <ul className="day-list">
                                  {week.detail.map((day: any, dayIndex: number) => (
                                    <li key={dayIndex} className="day-item">
                                      <strong>{day.dayname || `Day ${dayIndex + 1}`}</strong> {day.title || ""}
                                      {day.description && <p className="day-description">{day.description}</p>}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {week.keydeliverable && (
                                <div className="key-deliverable">
                                  <strong>Key Deliverables:</strong> {week.keydeliverable}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })}
                </div>
              </section>
            )}

            {course.skills && course.skills.length > 0 && (
              <section className="skills-section">
                <h2 className="section-title">Skills</h2>
                <div className="skills-container">
                  {course.skills.map((skill: any, index: number) => (
                    <span key={index} className="skill-tag">
                      {skill.skill_name || skill}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CourseDetailWeb;


